import { createRng } from "@/lib/seededRandom";
import type { Department } from "@/types";
import { EMPLOYEES } from "./employees";

const rng = createRng(778899);

function headcountFor(name: string) {
  return EMPLOYEES.filter((e) => e.department === name && e.status === "active").length;
}

const DEFINITIONS: Array<Omit<Department, "headcount">> = [
  {
    id: "dept-sales",
    name: "Sales",
    owner: "Marcus Ellison",
    status: "good",
    kpis: [
      { label: "New Logos (MTD)", value: "18", trend: "up" },
      { label: "Win Rate", value: "34%", trend: "up" },
      { label: "Pipeline Coverage", value: "3.2x", trend: "flat" },
    ],
    risks: ["Sales cycle lengthening for enterprise deals", "Two open territories unstaffed"],
    actionItems: ["Backfill enterprise AE role", "Refresh competitive battlecards"],
    aiSummary: "Pipeline is healthy and coverage exceeds target, but two unstaffed territories risk Q3 bookings.",
  },
  {
    id: "dept-marketing",
    name: "Marketing",
    owner: "Priya Chandran",
    status: "good",
    kpis: [
      { label: "MQLs (MTD)", value: "612", trend: "up" },
      { label: "CAC", value: "$410", trend: "down" },
      { label: "Content Output", value: "22 pcs", trend: "flat" },
    ],
    risks: ["Paid channel CPL creeping up", "Brand refresh behind schedule"],
    actionItems: ["Reallocate 15% of paid budget to organic", "Finalize brand refresh by EOQ"],
    aiSummary: "Lead volume is strong and CAC is improving; watch paid CPL trend over the next two weeks.",
  },
  {
    id: "dept-client-success",
    name: "Client Success",
    owner: "Renata Cruz",
    status: "at-risk",
    kpis: [
      { label: "Avg Health Score", value: "71", trend: "down" },
      { label: "NPS", value: "42", trend: "flat" },
      { label: "Escalations (MTD)", value: "9", trend: "up" },
    ],
    risks: ["Escalation volume up 3rd month running", "12 accounts below 60 health score"],
    actionItems: ["Launch at-risk account war room", "Rebalance AM book of business"],
    aiSummary: "Escalations are trending up for the third consecutive month — recommend a targeted intervention on the at-risk cohort.",
  },
  {
    id: "dept-service-delivery",
    name: "Service Delivery",
    owner: "Diego Villanueva",
    status: "good",
    kpis: [
      { label: "SLA Compliance", value: "93%", trend: "up" },
      { label: "Utilization", value: "88%", trend: "flat" },
      { label: "Replacement Requests", value: "6", trend: "down" },
    ],
    risks: ["Bench capacity thin for Healthcare vertical", "Two TLs over span-of-control threshold"],
    actionItems: ["Cross-train 8 VAs for Healthcare accounts", "Promote 2 senior VAs to TL"],
    aiSummary: "Delivery metrics are stable; the main watch item is thin bench coverage in the Healthcare vertical.",
  },
  {
    id: "dept-talent-acquisition",
    name: "Talent Acquisition",
    owner: "Isabella Ferreira",
    status: "at-risk",
    kpis: [
      { label: "Time to Hire", value: "34 days", trend: "up" },
      { label: "Open Roles", value: "22", trend: "up" },
      { label: "Offer Accept Rate", value: "81%", trend: "flat" },
    ],
    risks: ["Time to hire above 30-day target", "Sourcing pipeline thin for bilingual VAs"],
    actionItems: ["Launch referral incentive campaign", "Add sourcing agency for niche roles"],
    aiSummary: "Hiring velocity has slipped — time to hire is now above target with 22 roles open across departments.",
  },
  {
    id: "dept-people-culture",
    name: "People & Culture",
    owner: "Grace Lim",
    status: "good",
    kpis: [
      { label: "Attrition (Monthly)", value: "3.1%", trend: "down" },
      { label: "eNPS", value: "58", trend: "up" },
      { label: "Training Completion", value: "84%", trend: "up" },
    ],
    risks: ["Manager span-of-control above policy in 2 pods"],
    actionItems: ["Promote 2 team leads to reduce span-of-control"],
    aiSummary: "Engagement and attrition trends are both improving; span-of-control remains the one structural risk.",
  },
  {
    id: "dept-finance",
    name: "Finance",
    owner: "Andres Osei",
    status: "good",
    kpis: [
      { label: "Gross Margin", value: "61%", trend: "up" },
      { label: "DSO", value: "22 days", trend: "down" },
      { label: "Outstanding Invoices", value: "$184K", trend: "flat" },
    ],
    risks: ["Collections concentrated in 8 overdue accounts"],
    actionItems: ["Escalate 8 overdue accounts to AM for collection"],
    aiSummary: "Margins are healthy and DSO is improving; a small set of overdue accounts needs collections follow-up.",
  },
  {
    id: "dept-learning-development",
    name: "Learning & Development",
    owner: "Hannah Reyes",
    status: "excellent",
    kpis: [
      { label: "Courses Completed", value: "1,240", trend: "up" },
      { label: "Certification Rate", value: "76%", trend: "up" },
      { label: "Manager Training", value: "92%", trend: "flat" },
    ],
    risks: ["New-hire onboarding curriculum overdue for refresh"],
    actionItems: ["Refresh onboarding curriculum for Q3 cohort"],
    aiSummary: "L&D metrics are strong across the board; onboarding content is the only item due for a refresh.",
  },
  {
    id: "dept-technology-ai",
    name: "Technology / AI",
    owner: "Kevin Tan",
    status: "excellent",
    kpis: [
      { label: "Automation Coverage", value: "47%", trend: "up" },
      { label: "AI Agent Uptime", value: "99.7%", trend: "flat" },
      { label: "Tickets Automated", value: "38%", trend: "up" },
    ],
    risks: ["Legacy reporting pipeline still manual for 3 clients"],
    actionItems: ["Migrate remaining 3 clients off manual reporting"],
    aiSummary: "AI rollout is ahead of plan; remaining manual reporting workflows are the last automation gap.",
  },
];

export const DEPARTMENTS: Department[] = DEFINITIONS.map((d) => ({
  ...d,
  headcount: headcountFor(d.name) || rng.int(8, 20),
}));
